import { Dropdown, DropdownItem, Icon, ToggleRow } from '@sorrem-client/ui/components';
import type { UIDebugOverlaySettings } from '@sorrem-client/types';
import type { DebugOverlayControls } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import styles from './DeveloperSettingsSection.module.scss';

interface DeveloperSettingsSectionProps {
    /** The debug overlays shown, kept in this browser only. */
    settings: UIDebugOverlaySettings;
    onChange: (nextSettings: Partial<UIDebugOverlaySettings>) => void;
    /** The debug overlay page's HUD data controls, when there. */
    debugControls?: DebugOverlayControls;
}

/**
 * The settings pop-over's Developer section in a run, shown only to the
 * developer role (#203): debug overlay toggles, and on the debug overlay
 * page the HUD data controls.
 */
export function DeveloperSettingsSection({ settings, onChange, debugControls }: DeveloperSettingsSectionProps): JSX.Element {
    const availablePartyMembers = debugControls?.characters.filter((character) => {
        return character.id !== debugControls.selectedCharacterId
            && !debugControls.partyMembers.some((member) => member.id === character.id);
    }) ?? [];

    return (
        <section className={styles.developerSettingsSection} aria-labelledby="settings-developer-title">
            <h2 id="settings-developer-title" className={styles.sectionTitle}>Developer</h2>
            <ToggleRow
                className={styles.toggleRow}
                id="game-setting-telemetry"
                label="Telemetry"
                checked={settings.telemetryEnabled}
                onChange={(checked) => onChange({ telemetryEnabled: checked })}
            />
            <ToggleRow
                className={styles.toggleRow}
                id="game-setting-entity-info"
                label="Entity information"
                checked={settings.entityInfoEnabled}
                onChange={(checked) => onChange({ entityInfoEnabled: checked })}
            />

            {debugControls !== undefined && (
                <section className={styles.debugControls} aria-labelledby="debug-hud-data-title">
                    <h3 id="debug-hud-data-title">HUD data</h3>

                    <div className={styles.controlGroup}>
                        <label htmlFor="debug-hud-character">Character</label>
                        <Dropdown
                            id="debug-hud-character"
                            value={debugControls.selectedCharacterId ?? ''}
                            onChange={(event) => debugControls.onSelectCharacter(event.target.value)}
                            disabled={debugControls.characters.length === 0}
                        >
                            {debugControls.characters.length === 0 && (
                                <DropdownItem value="">No characters available</DropdownItem>
                            )}
                            {debugControls.characters.map((character) => (
                                <DropdownItem key={character.id} value={character.id}>
                                    {character.name}
                                </DropdownItem>
                            ))}
                        </Dropdown>
                    </div>

                    <div className={styles.controlGroup}>
                        <span className={styles.controlLabel}>Party</span>
                        {debugControls.partyMembers.length === 0 ? (
                            <p className={styles.emptyParty}>No party members</p>
                        ) : (
                            <ul className={styles.partyList}>
                                {debugControls.partyMembers.map((member) => (
                                    <li key={member.id} className={styles.partyMember}>
                                        <span>
                                            <strong>{member.name}</strong>
                                        </span>
                                        <Icon
                                            icon="decline"
                                            tooltip={`Remove ${member.name} from party`}
                                            onClick={() => debugControls.onRemovePartyMember(member.id)}
                                        />
                                    </li>
                                ))}
                            </ul>
                        )}

                        <Dropdown
                            id="debug-hud-party-member"
                            value=""
                            onChange={(event) => {
                                if (event.target.value !== '') {
                                    debugControls.onAddPartyMember(event.target.value);
                                }
                            }}
                            disabled={availablePartyMembers.length === 0}
                        >
                            <DropdownItem value="">
                                {availablePartyMembers.length === 0 ? 'No party members available' : 'Add party member'}
                            </DropdownItem>
                            {availablePartyMembers.map((character) => (
                                <DropdownItem key={character.id} value={character.id}>
                                    {character.name}
                                </DropdownItem>
                            ))}
                        </Dropdown>
                    </div>
                </section>
            )}
        </section>
    );
}
